import { logError } from "@/lib/log";
import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Bell, Repeat, Receipt, AlertTriangle, Crown, CreditCard, CalendarPlus, BarChart3 } from "lucide-react";

const KIND_ICON = {
  recurring_created: Repeat,
  bill_due: Receipt,
  bill_overdue: AlertTriangle,
  bill_recreated: CalendarPlus,
  payment_success: CreditCard,
  premium_activated: Crown,
  trial_ending: Crown,
  trial_ended: Crown,
  weekly_digest: BarChart3,
};
const KIND_COLOR = {
  recurring_created: "#2E6F40",
  bill_due: "#A67C00",
  bill_overdue: "#D94F3D",
  bill_recreated: "#2E6F40",
  payment_success: "#2E6F40",
  premium_activated: "#A67C00",
  trial_ending: "#A67C00",
  trial_ended: "#D94F3D",
  weekly_digest: "#2E6F40",
};

function timeAgo(iso) {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function NotificationItem({ notification: n, index }) {
  const Icon = KIND_ICON[n.kind] || Bell;
  const color = KIND_COLOR[n.kind] || "#6C7565";
  return (
    <div data-testid={`notif-item-${index}`} className={`px-4 py-3 border-b border-[#EAE7DD] last:border-0 flex gap-3 ${n.read ? "opacity-60" : "bg-white"}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
      <div className="min-w-0">
        <div className="text-sm font-medium leading-snug">{n.title}</div>
        <div className="text-xs text-[#6C7565] mt-0.5 leading-snug">{n.detail}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565] mt-1">{timeAgo(n.created_at)}</div>
      </div>
    </div>
  );
}

function NotificationPanel({ items, unread, onMarkAllRead }) {
  return (
    <div data-testid="notif-panel" className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-[#F7F5F0] border border-[#1A1F16] shadow-xl z-50">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#D8D4C7]">
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">Notifications</span>
        {unread > 0 && (
          <button data-testid="notif-mark-all" onClick={onMarkAllRead} className="font-mono text-[10px] uppercase tracking-widest text-[#A67C00] hover:text-[#1A1F16] underline underline-offset-2">
            Mark all read
          </button>
        )}
      </div>
      <div className="max-h-96 overflow-y-auto">
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-[#6C7565] italic" data-testid="notif-empty">All quiet. No reminders.</div>
        ) : (
          items.map((n, i) => <NotificationItem key={n.id} notification={n} index={i} />)
        )}
      </div>
    </div>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch (e) {
      logError("Notifications load failed", e);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    const onSaved = () => load();
    window.addEventListener("voice-expense:saved", onSaved);
    return () => { clearInterval(id); window.removeEventListener("voice-expense:saved", onSaved); };
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) {
      logError("Mark-all-read failed", e);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        data-testid="notif-bell"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 border border-[#1A1F16] hover:bg-[#1A1F16] hover:text-[#F7F5F0] transition-colors"
        title="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D94F3D] text-white text-[10px] font-mono flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && <NotificationPanel items={items} unread={unread} onMarkAllRead={markAllRead} />}
    </div>
  );
}
